import React, { useEffect, useState } from "react";
import { api, formatError, API, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, RefreshCw, Facebook, Instagram, Globe, FileSpreadsheet, ShieldCheck, CheckCircle2, XCircle, Download, Zap, Unlink, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";

const CodeBlock = ({ code, id }) => (
  <div className="relative bg-slate-900 rounded-lg p-4 text-xs text-slate-100 font-mono overflow-x-auto" data-testid={id}>
    <pre className="whitespace-pre-wrap break-all">{code}</pre>
    <button
      onClick={() => { navigator.clipboard.writeText(code); toast.success("Copied to clipboard"); }}
      className="absolute top-2 right-2 bg-amber-500 hover:bg-amber-600 text-white text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-md font-semibold"
      data-testid={`copy-${id}`}
    >
      Copy
    </button>
  </div>
);

const Field = ({ label, value, copyKey }) => (
  <div>
    <Label className="text-xs font-semibold text-slate-600">{label}</Label>
    <div className="mt-1 flex gap-2">
      <Input readOnly value={value || ""} className="font-mono text-xs" data-testid={`field-${copyKey}`}/>
      <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(value || ""); toast.success("Copied"); }} data-testid={`copy-field-${copyKey}`}>
        <Copy className="w-3 h-3"/>
      </Button>
    </div>
  </div>
);

export default function Integrations() {
  const [cfg, setCfg] = useState(null);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [oauth, setOauth] = useState({ available: false });
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    const { data } = await api.get("/integrations/leads");
    setCfg(data);
    setShowManual(!data?.meta_page_id);
    try { setUsers((await api.get("/users")).data.filter(u => u.role === "sales" || u.role === "admin")); } catch {}
    try { setOauth((await api.get("/integrations/meta/oauth-config")).data); } catch {}
  };
  useEffect(() => { load(); }, []);

  // Handle OAuth redirect result
  useEffect(() => {
    const status = params.get("meta");
    const err = params.get("meta_error");
    const page = params.get("page");
    if (status === "connected") {
      toast.success(`Facebook Page connected${page ? ": " + page : ""}!`);
      params.delete("meta"); params.delete("page"); setParams(params, { replace: true });
      load();
    } else if (err) {
      toast.error(`Facebook connect failed: ${err}`);
      params.delete("meta_error"); setParams(params, { replace: true });
    }
  }, [params]);

  if (!cfg) return <div className="text-slate-400">Loading...</div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/integrations/leads", cfg);
      toast.success("Integration settings saved");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const rotate = async () => {
    if (!window.confirm("Rotate webhook token? All existing website / Google Form / Zapier setups will stop working until you update them.")) return;
    const { data } = await api.post("/integrations/leads/rotate-token");
    setCfg({ ...cfg, webhook_token: data.webhook_token });
    toast.success("Token rotated — update your integrations");
  };

  const validate = async () => {
    setValidating(true);
    try {
      await api.put("/integrations/leads", cfg); // ensure latest token stored
      const { data } = await api.post("/integrations/meta/validate");
      toast.success(`Connected: ${data.page_name} · leadgen subscribed: ${data.leadgen_subscribed ? "yes" : "no"}`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Validation failed"); }
    finally { setValidating(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect this Facebook Page? Incoming lead ads will stop until reconnected.")) return;
    try {
      await api.post("/integrations/meta/disconnect");
      toast.success("Facebook Page disconnected");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const importExisting = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/integrations/meta/import-existing");
      toast.success(`Imported ${data.imported} new leads · Skipped ${data.skipped} duplicates`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Import failed"); }
    finally { setImporting(false); }
  };

  const startOAuth = () => {
    // Redirect to backend OAuth start; browser will follow to facebook.com then back.
    window.location.href = `${API}/integrations/meta/oauth/start`;
  };

  const inbox = (src) => `${API}/leads/inbox/${cfg.webhook_token}?source=${encodeURIComponent(src)}`;
  const metaCallback = `${API}/leads/webhook/meta`;

  const websiteSnippet = `<!-- MRM Renewables — Website Lead Form -->
<form id="mrm-lead-form" style="max-width:400px;font-family:sans-serif">
  <input name="name" placeholder="Your Name" required style="width:100%;padding:10px;margin:6px 0;border:1px solid #ddd;border-radius:6px"/>
  <input name="mobile" placeholder="Mobile Number" required style="width:100%;padding:10px;margin:6px 0;border:1px solid #ddd;border-radius:6px"/>
  <input name="email" placeholder="Email" style="width:100%;padding:10px;margin:6px 0;border:1px solid #ddd;border-radius:6px"/>
  <input name="city" placeholder="City" style="width:100%;padding:10px;margin:6px 0;border:1px solid #ddd;border-radius:6px"/>
  <input name="solar_requirement" placeholder="Solar Requirement (e.g. 5 kW)" style="width:100%;padding:10px;margin:6px 0;border:1px solid #ddd;border-radius:6px"/>
  <textarea name="notes" placeholder="Message" style="width:100%;padding:10px;margin:6px 0;border:1px solid #ddd;border-radius:6px"></textarea>
  <button type="submit" style="width:100%;padding:12px;background:#F59E0B;color:#fff;border:0;border-radius:6px;font-weight:700">Get Free Solar Quote</button>
</form>
<script>
document.getElementById("mrm-lead-form").addEventListener("submit", async (e) => {
  e.preventDefault();
  const data = Object.fromEntries(new FormData(e.target));
  try {
    const r = await fetch("${inbox("Website")}", {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data)
    });
    if (r.ok) { alert("Thank you! Our team will contact you shortly."); e.target.reset(); }
    else alert("Something went wrong. Please try again or call us.");
  } catch { alert("Network error. Please try again."); }
});
</script>`;

  const googleAppsScript = `// MRM Renewables — Google Form → CRM
function onFormSubmit(e) {
  var responses = e.namedValues || {};
  function first(key) {
    for (var k in responses) if (k.toLowerCase().indexOf(key.toLowerCase()) !== -1) return responses[k][0];
    return "";
  }
  var payload = {
    name: first("name"),
    mobile: first("mobile") || first("phone"),
    email: first("email"),
    city: first("city"),
    solar_requirement: first("kw") || first("requirement") || first("capacity"),
    property_type: first("property"),
    notes: first("message") || first("comment"),
  };
  UrlFetchApp.fetch("${inbox("Google Form")}", {
    method: "post",
    contentType: "application/json",
    payload: JSON.stringify(payload),
    muteHttpExceptions: true,
  });
}`;

  const curlExample = `curl -X POST "${inbox("Website")}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Rajesh Kumar",
    "mobile": "9876543210",
    "email": "rajesh@example.com",
    "city": "Jaipur",
    "solar_requirement": "5 kW",
    "notes": "Interested in residential rooftop"
  }'`;

  const connected = !!cfg.meta_page_id;

  const MetaConnectPanel = ({ platform }) => (
    <Card><CardContent className="p-6 space-y-5">
      <div>
        <h3 className="font-bold text-slate-900 text-lg" style={{fontFamily:'Outfit'}}>
          {platform === "facebook" ? "Facebook Lead Ads" : "Instagram Lead Ads"}
        </h3>
        <p className="text-slate-500 text-sm mt-1">
          Facebook aur Instagram same Meta page ke through connect hote hain. Ek baar page connect ho jaye — dono ke lead ads yahan auto-capture ho jayenge.
        </p>
      </div>

      {/* Connection status card */}
      <div className={`rounded-xl p-4 flex items-start gap-3 ${connected ? "bg-emerald-50 border border-emerald-200" : "bg-rose-50 border border-rose-200"}`} data-testid={`meta-status-${platform}`}>
        <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${connected ? "bg-emerald-500" : "bg-rose-500"}`}>
          {connected ? <CheckCircle2 className="w-6 h-6 text-white"/> : <XCircle className="w-6 h-6 text-white"/>}
        </div>
        <div className="flex-1 min-w-0">
          <div className={`font-bold ${connected ? "text-emerald-900" : "text-rose-900"}`}>
            {connected ? "Page Connected" : "Not Connected Yet"}
          </div>
          {connected ? (
            <div className="text-sm text-emerald-800 mt-0.5">
              <span className="font-semibold" data-testid="meta-page-name">{cfg.meta_page_name}</span>
              {cfg.meta_page_category && <span className="opacity-80"> · {cfg.meta_page_category}</span>}
              <div className="text-[11px] opacity-70 mt-0.5">
                Page ID: <span className="font-mono">{cfg.meta_page_id}</span>
                {cfg.meta_connected_at && <> · Connected {fmtDate(cfg.meta_connected_at)}</>}
                {cfg.meta_leadgen_subscribed ? " · leadgen webhook: ON ✓" : " · leadgen webhook: OFF ⚠️"}
              </div>
              {cfg.meta_last_import_at && (
                <div className="text-[11px] opacity-70">Last import: {fmtDate(cfg.meta_last_import_at)}{cfg.meta_last_import_count !== undefined ? ` (${cfg.meta_last_import_count} leads)` : ""}</div>
              )}
            </div>
          ) : (
            <div className="text-sm text-rose-800 mt-0.5">Connect your Facebook Page so new Lead Ads flow into this CRM automatically.</div>
          )}
        </div>
        {connected && (
          <div className="flex flex-col gap-1.5">
            <Button size="sm" onClick={importExisting} disabled={importing} className="bg-slate-900 text-white hover:bg-slate-800" data-testid={`meta-import-${platform}`}>
              <Download className="w-3.5 h-3.5 mr-1"/>{importing ? "Importing..." : "Import Existing"}
            </Button>
            <Button size="sm" variant="outline" onClick={disconnect} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`meta-disconnect-${platform}`}>
              <Unlink className="w-3.5 h-3.5 mr-1"/>Disconnect
            </Button>
          </div>
        )}
      </div>

      {/* One-click OAuth (if configured) */}
      {oauth.available ? (
        <div className="bg-gradient-to-br from-[#E7F3FF] to-white border border-[#1877F2]/30 rounded-xl p-5" data-testid="meta-oauth-panel">
          <div className="flex items-center gap-2 text-[#1877F2] font-bold text-sm uppercase tracking-widest"><Zap className="w-4 h-4"/> One-Click Connect</div>
          <p className="text-sm text-slate-700 mt-2 mb-4">
            Aap ka Meta App configured hai. Neeche button dabao, Facebook par sign-in karo, apni Page choose karo — bas. Sales bhi khud page connect kar sakenge.
          </p>
          <Button className="bg-[#1877F2] hover:bg-[#166FE5] text-white font-semibold" onClick={startOAuth} data-testid={`meta-oauth-btn-${platform}`}>
            <Facebook className="w-4 h-4 mr-2"/>{connected ? "Reconnect / Switch Page" : "Connect Facebook Page"}
          </Button>
        </div>
      ) : (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-600">
          <div className="font-semibold text-slate-900 mb-1 flex items-center gap-1"><Zap className="w-3.5 h-3.5 text-amber-500"/> One-Click Connect not configured</div>
          <p>Admin ko ek baar Meta App banana hoga (5 min): <a href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer" className="text-amber-700 underline">developers.facebook.com/apps</a>. Phir ye 3 env vars set karo — <code className="bg-white px-1 rounded">META_APP_ID</code>, <code className="bg-white px-1 rounded">META_APP_SECRET</code>, <code className="bg-white px-1 rounded">META_REDIRECT_URI</code> — aur ye page reload karo. Meanwhile aap manual token flow use kar sakte ho ⬇️</p>
        </div>
      )}

      {/* Manual token flow (collapsible) */}
      <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
        <button
          type="button"
          onClick={()=>setShowManual(v=>!v)}
          className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-slate-900 hover:bg-slate-50"
          data-testid={`toggle-manual-${platform}`}
        >
          <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-slate-500"/>Manual Token Setup {connected ? "(alternative)" : ""}</span>
          <ChevronDown className={`w-4 h-4 transition ${showManual?"rotate-180":""}`}/>
        </button>
        {showManual && (
          <div className="px-4 pb-4 pt-1 space-y-4 border-t border-slate-100">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-slate-700 space-y-1">
              <div className="font-semibold text-amber-900">Ye tab use karo agar OAuth setup nahi karna</div>
              <ol className="list-decimal ml-5 space-y-0.5">
                <li>Meta App banao (developers.facebook.com/apps → Business type)</li>
                <li>Webhooks product add karo → Page → leadgen → paste Callback URL + Verify Token below</li>
                <li>Marketing API product add karo → generate long-lived Page Access Token</li>
                <li>Token neeche paste karke "Test & Auto-Subscribe" dabao</li>
              </ol>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Webhook Callback URL" value={metaCallback} copyKey="meta-callback"/>
              <Field label="Verify Token" value={cfg.meta_verify_token} copyKey="meta-verify"/>
              <div className="sm:col-span-2">
                <Label className="text-xs font-semibold text-slate-600">Page Access Token (long-lived)</Label>
                <Textarea rows={2} value={cfg.meta_page_access_token || ""} onChange={e=>setCfg({...cfg, meta_page_access_token:e.target.value})} placeholder="EAAG..." className="mt-1 font-mono text-xs" data-testid="meta-page-token-input"/>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs font-semibold text-slate-600">App Secret (optional, for signature verification)</Label>
                <Input type="password" value={cfg.meta_app_secret || ""} onChange={e=>setCfg({...cfg, meta_app_secret:e.target.value})} className="mt-1 font-mono text-xs" data-testid="meta-app-secret-input"/>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid={`save-${platform}-btn`}>
                {saving ? "Saving..." : "Save Token"}
              </Button>
              <Button variant="outline" onClick={validate} disabled={validating || !cfg.meta_page_access_token} data-testid={`meta-validate-${platform}`}>
                <ShieldCheck className="w-4 h-4 mr-1"/>{validating ? "Testing..." : "Test & Auto-Subscribe"}
              </Button>
            </div>
          </div>
        )}
      </div>

      {connected && (
        <div className="flex items-center gap-2 text-emerald-700 text-sm bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <CheckCircle2 className="w-4 h-4"/>
          <span>New {platform === "facebook" ? "Facebook" : "Instagram"} Lead Ad submissions se leads auto-capture ho rahe hain (source = <b>{platform === "facebook" ? "Facebook" : "Instagram"}</b>).</span>
        </div>
      )}
    </CardContent></Card>
  );

  return (
    <div className="space-y-6 max-w-4xl" data-testid="integrations-page">
      <div>
        <h1 className="text-3xl font-extrabold text-slate-900" style={{fontFamily:'Outfit'}}>Lead Integrations <span className="text-slate-400 text-lg font-medium">/ लीड्स सेटअप</span></h1>
        <p className="text-slate-500 text-sm">Auto-capture leads from Facebook, Instagram, Website & Google Form directly into your CRM.</p>
      </div>

      {/* Common settings */}
      <Card><CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2 text-slate-900 font-semibold"><ShieldCheck className="w-4 h-4 text-amber-500"/> Common Settings</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2 flex gap-2 items-end">
            <div className="flex-1"><Field label="Website / Google Form Webhook Token" value={cfg.webhook_token} copyKey="webhook-token" /></div>
            <Button variant="outline" onClick={rotate} className="mb-0" data-testid="rotate-token-btn"><RefreshCw className="w-3 h-3 mr-1"/>Rotate</Button>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600">Auto-Assign New Leads To</Label>
            <Select value={cfg.default_assignee_id || "none"} onValueChange={v => setCfg({...cfg, default_assignee_id: v === "none" ? null : v})}>
              <SelectTrigger className="mt-1" data-testid="default-assignee-select"><SelectValue placeholder="Unassigned"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Unassigned (queue for admin)</SelectItem>
                {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600">Auto Status</Label>
            <Select value={cfg.auto_status || "New"} onValueChange={v => setCfg({...cfg, auto_status: v})}>
              <SelectTrigger className="mt-1" data-testid="auto-status-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                {["New","Contacted","Follow-up"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="save-integrations-btn">
          {saving ? "Saving..." : "Save Common Settings"}
        </Button>
      </CardContent></Card>

      <Tabs defaultValue="facebook">
        <TabsList className="grid grid-cols-4 w-full max-w-2xl">
          <TabsTrigger value="facebook" data-testid="tab-facebook"><Facebook className="w-3.5 h-3.5 mr-1.5"/>Facebook</TabsTrigger>
          <TabsTrigger value="instagram" data-testid="tab-instagram"><Instagram className="w-3.5 h-3.5 mr-1.5"/>Instagram</TabsTrigger>
          <TabsTrigger value="website" data-testid="tab-website"><Globe className="w-3.5 h-3.5 mr-1.5"/>Website</TabsTrigger>
          <TabsTrigger value="google" data-testid="tab-google"><FileSpreadsheet className="w-3.5 h-3.5 mr-1.5"/>Google Form</TabsTrigger>
        </TabsList>

        <TabsContent value="facebook"><MetaConnectPanel platform="facebook"/></TabsContent>
        <TabsContent value="instagram"><MetaConnectPanel platform="instagram"/></TabsContent>

        <TabsContent value="website">
          <Card><CardContent className="p-6 space-y-5">
            <div>
              <h3 className="font-bold text-slate-900 text-lg" style={{fontFamily:'Outfit'}}>Website Lead Form</h3>
              <p className="text-slate-500 text-sm mt-1">Paste this HTML anywhere on your website — leads will flow directly into your CRM.</p>
            </div>
            <Field label="Webhook Endpoint" value={inbox("Website")} copyKey="website-url"/>
            <div>
              <Label className="text-xs font-semibold text-slate-600">HTML + JS Snippet (drop-in ready)</Label>
              <div className="mt-1"><CodeBlock code={websiteSnippet} id="website-snippet"/></div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Test with cURL</Label>
              <div className="mt-1"><CodeBlock code={curlExample} id="curl-snippet"/></div>
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="google">
          <Card><CardContent className="p-6 space-y-5">
            <div>
              <h3 className="font-bold text-slate-900 text-lg" style={{fontFamily:'Outfit'}}>Google Form → CRM</h3>
              <p className="text-slate-500 text-sm mt-1">Add this Apps Script to any Google Form. Every submission will create a lead in your CRM.</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-slate-700">
              <div className="font-semibold mb-2 text-amber-900">Setup Steps</div>
              <ol className="list-decimal ml-5 space-y-1">
                <li>Open your Google Form → click the <b>⋮ menu → Script editor</b>.</li>
                <li>Delete any placeholder code, paste the snippet below, and click <b>Save</b>.</li>
                <li>Click the <b>⏰ clock icon</b> (Triggers) → Add Trigger → Choose function <code className="bg-white px-1 rounded">onFormSubmit</code>, event type <b>From form → On form submit</b>.</li>
                <li>Grant permission when prompted. Done — try a test submission!</li>
              </ol>
            </div>
            <Field label="Webhook Endpoint" value={inbox("Google Form")} copyKey="google-url"/>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Google Apps Script</Label>
              <div className="mt-1"><CodeBlock code={googleAppsScript} id="google-snippet"/></div>
            </div>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
